import React, { useState, forwardRef, useRef } from "react";
import { useTranslation } from "react-i18next";
import { composeRefs, useControlled } from "@exonn/hooks";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { InputErrorMessage } from "../InputErrorMessage/InputErrorMessage.tsx";
import * as styles from "./TextArea.styles.ts";
import type { ChangeEvent, Ref } from "react";
import type { TextareaProps } from "./TextArea.types.ts";

import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";
import { Flex } from "src/components/Flex.tsx";
import { Placeholders } from "src/components/InputPlaceholders/InputPlaceholders.tsx";
import { useTextareaInsertion } from "src/components/TextArea/useTextAreaInsertion.tsx";
import type { TextTemplatePlaceholder } from "@exonn/js-sdk";
import { InputLabel } from "src/components/InputLabel/InputLabel.tsx";

await loadTranslations(TranslationNamespaceEnum.TextAreaComponent);

const TextAreaComponent = (
  props: TextareaProps,
  ref: Ref<HTMLTextAreaElement>,
) => {
  const [charCount, setCharCount] = useState(0);
  const { t } = useTranslation();

  const {
    value: controlledValue,
    label,
    helperText,
    floatingError = false,
    placeholders = [],
    required,
    onSelectPlaceholder,
    defaultValue,
    error,
    resize = "none",
    placeholder = t("placeholder", {
      ns: TranslationNamespaceEnum.TextAreaComponent,
    }),
    maxLength,
    disabled,
    className,
    onChange,
    isLoading = false,
    ...rest
  } = props;

  const [value, setValue] = useControlled({
    controlled: controlledValue,
    default: defaultValue,
    name: "TextArea",
    state: "value",
  });

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    maxLength && setCharCount(e.target.value.length);
    setValue(e.target.value);
    onChange?.(e);
  };

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { saveCursor, restoreCursor, insertText } = useTextareaInsertion();

  const handleSelectPlaceholder = (placeholder: TextTemplatePlaceholder) => {
    onSelectPlaceholder?.(placeholder);

    if (!inputRef.current) return;

    const code =
      placeholder.type === "conditional"
        ? `{% if ${placeholder.value} %} ... {% endif %}`
        : `{{ ${placeholder.value} }}`;

    const newValue = insertText(inputRef.current, value ?? "", code);

    const syntheticEvent = {
      currentTarget: {
        value: newValue,
        name: (rest as any)?.name,
      },
      target: {
        value: newValue,
        name: (rest as any)?.name,
      },
    } as unknown as ChangeEvent<HTMLTextAreaElement>;
    handleChange?.(syntheticEvent);

    restoreCursor(inputRef.current);
  };

  return (
    <Flex flexDirection="column" gap={"15px"}>
      <Placeholders
        onSelectPlaceholder={handleSelectPlaceholder}
        placeholders={placeholders}
      />
      <Skeleton isLoading={isLoading}>
        <div css={styles.rootContainer} className={className}>
          {label && (
            <InputLabel
              label={label}
              required={required}
              helperText={helperText}
            />
          )}

          <textarea
            ref={composeRefs(ref, inputRef)}
            css={styles.textareaStyles({ error, resize, disabled })}
            maxLength={maxLength}
            disabled={disabled}
            placeholder={placeholder}
            value={value}
            onSelect={(e) => saveCursor(e.currentTarget)}
            defaultValue={defaultValue}
            onChange={handleChange}
            {...rest}
          />

          {maxLength && !disabled && (
            <p css={styles.helperTextStyles}>
              {charCount}/{maxLength}
            </p>
          )}

          {error && (
            <InputErrorMessage floating={floatingError} message={error} />
          )}
        </div>
      </Skeleton>
    </Flex>
  );
};

export const TextArea = forwardRef(TextAreaComponent);
